import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Typography, withStyles } from '@material-ui/core';
import classNames from 'classnames';
import PreviewDescriptionDialog from '../../shared/previewDescriptionDialog';
import Filters from '../../shared/filters';
import NotificationCard from '../../shared/notificationCard';
import Search from '../../shared/search';
import CustomButton from '../../shared/customButton';
import CustomFormDrawer from '../../shared/customFormDrawer';
import JobTitleItem from '../../shared/jobTitleItem';
import AlertDialog from '../../shared/alertDialog';
import PlaceholderButton from '../../shared/placeholderButton';
import { ReactComponent as PlusIconWhite } from '../../../assets/icons/plus-icon-white.svg';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { ASSIGN_JOB_TITLE_TO_FORM_FIELDS } from '../../../constants/jobTitle';
import { USER_STATUSES } from '../../../constants/statuses';
import { sticky } from '../../../constants/helperCssRules';
import { GENERAL_CATEGORY_ID } from '../../../constants/tags';
import {
  PEOPLE_DEFAULT_ORDERING,
  USER_EMPLOYMENT_INFORMATION,
} from '../../../constants/people';
import {
  PARAMS,
  PAGE_WHITELISTED_PARAMS,
  JOB_TITLES_DEFAULT_PARAMS,
} from '../../../constants/pages';
import { SYSTEM_ROLES } from '../../../constants/roles';
import {
  API_POSITIONS,
  api_position,
  API_USERS_BASIC,
  API_IMPORT_POSITIONS,
  API_USERS_MULTIPLE,
} from '../../../constants/apiRoutes';
import http from '../../../utility/http';
import {
  trimString,
  isArrayEmpty,
  isArray,
  getObjectToNumberArray,
  isObjectEmpty,
} from '../../../utility/helpers';
import {
  EVENT_ACTION_TYPES,
  tagManagerDataLayer,
} from '../../../utility/tagManager';
import {
  parseDuplicateParameters,
  showSuccessMessage,
  parseQueryParams,
  prepareUsersForUpdate,
  hasSelectedFiltersCheck,
} from '../../../utility/uiUtils';
import { onSaveCreatableTag } from '../../../utility/tagUtils';
import {
  prepareJobTitleFields,
  preparePageFilters,
  prepareJobTitleTypeOptions,
  prepareActiveOptions,
  jobFamilyLabel,
  criticalityLabel,
  INITIAL_JOB_TITLE_DATA,
} from './config';
import { getInitialAssignedToUsersData } from '../../../utility/jobTitle';
import {
  hasAuthPermissionCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';

const styles = ({ palette: { primary }, spacing }) => ({
  form: {
    display: 'grid',
    gridColumnGap: 24,
    gridTemplateColumns: 'minmax(0, 1fr) minmax(160px, 1fr) minmax(120px, 1fr)',
  },
  formWithSuccession: {
    gridTemplateColumns:
      'minmax(0, 1fr) minmax(120px, 1fr) minmax(60px, auto) minmax(120px, 1fr)',
    gridTemplateAreas: `
      "name name . ."
      "family type keyJob criticality"
      "tags tags . ."
      "description description description description"
    `,
  },
  formWithoutSuccession: {
    gridTemplateAreas: `
      "name name name"
      "tags . ."
      "description description description"
    `,
  },
  name: {
    gridArea: 'name',
  },
  family: {
    gridArea: 'family',
  },
  type: {
    gridArea: 'type',
  },
  keyJob: {
    gridArea: 'keyJob',
  },
  criticality: {
    alignSelf: 'start',
    gridArea: 'criticality',
  },
  keyJobCheckbox: {
    '& > div': {
      marginTop: spacing(2),
    },
  },
  tags: {
    gridArea: 'tags',
  },
  descriptionSection: {
    gridArea: 'description',
    '& .ql-container': {
      height: 376,
    },
    '& > div': {
      height: 445,
    },
  },
  stickySubHeader: {
    ...sticky(primary.white, 105),
  },
  subHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing(8, 0, 4, 0),
  },
  description: {
    padding: spacing(8, 0, 6, 0),
  },
  actionsWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  search: {
    width: 300,
  },
  addButton: {
    marginLeft: spacing(2),
    width: 'max-content',
  },
  jobTitles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridGap: spacing(2),
  },
  drawerContent: {
    height: '100%',
  },
});

const { SEARCH, TAGS, EXCLUDE_STATUS, EXCLUDE_ROLE } = PARAMS;

class JobTitlesPage extends PureComponent {
  state = {
    isInitialLoad: true,
    isLoading: false,
    isLoadingImport: false,
    isOpened: false,
    isEdit: false,
    isDialogOpened: false,
    isAssignDialogOpened: false,
    [SEARCH]: '',
    filters: {
      [TAGS]: [],
    },
    initialData: INITIAL_JOB_TITLE_DATA,
    isDelete: false,
    jobTitle: null,
    jobTitleUsers: [],
    assignableUsers: [],
  };

  componentDidMount() {
    this.getInitialData();
  }

  componentWillUnmount() {
    const {
      clearJobTitles,
      clearTagsCategories,
      clearPageQuickFilters,
      clearSuccessionSettings,
    } = this.props;

    clearJobTitles();
    clearTagsCategories();
    clearPageQuickFilters();
    clearSuccessionSettings();
  }

  getInitialData = async () => {
    const {
      location,
      accessControl,
      organizationSettings,
      getPageQuickFilters,
      getTagsCategories,
      getAllUsers,
      getSuccessionJobFamilies,
      getSuccessionCriticality,
    } = this.props;
    const { users_see_succession } = organizationSettings;
    const canManageJobTitleUsers = hasAuthPermissionCheck(
      PERMISSION_CODES.JOB_TITLES_USERS_MANAGE,
      accessControl
    );
    const canManageSuccessionSettings = hasAuthPermissionCheck(
      PERMISSION_CODES.SUCCESSION_MANAGE,
      accessControl
    );

    const params = parseQueryParams(
      location.search,
      PAGE_WHITELISTED_PARAMS.JOB_TITLES
    );

    if (canManageJobTitleUsers) {
      const payload = await getAllUsers({
        [PARAMS.RBAC_SCOPE_PERMISSION]:
          PERMISSION_CODES.JOB_TITLES_USERS_MANAGE,
      });

      this.setState({ assignableUsers: payload });
    }

    const loadPageData = () => {
      const requests = [
        this.handleGetJobTitles(),
        getTagsCategories(),
        getPageQuickFilters(),
      ];

      if (users_see_succession && canManageSuccessionSettings) {
        requests.push(getSuccessionJobFamilies());
        requests.push(getSuccessionCriticality());
      }

      return Promise.all(requests).then(() => {
        this.setState({ isInitialLoad: false });
      });
    };

    if (!isObjectEmpty(params)) {
      return this.setState(
        {
          [SEARCH]: params[SEARCH] ? decodeURIComponent(params[SEARCH]) : '',
          filters: {
            [TAGS]: params[TAGS]
              ? [...(isArray(params[TAGS]) ? params[TAGS] : [params[TAGS]])]
              : [],
          },
        },
        loadPageData
      );
    }

    return loadPageData();
  };

  handleGetJobTitles = () => {
    const { navigate, location, getJobTitles } = this.props;
    const { filters, search } = this.state;

    const params = {
      ...filters,
      ...(search ? { search } : {}),
    };
    const query = parseDuplicateParameters(params);

    if (location.search !== `?${query}`) {
      navigate(`/job-titles${query ? `/?${query}` : ''}`, { replace: true });
    }

    return getJobTitles(params).then(() => this.setState({ isLoading: false }));
  };

  canAccessUserProfileCheck = user => {
    const { auth, accessControl } = this.props;

    return isUserProfileAccessibleCheck(accessControl, auth.id, user.id);
  };

  getUsersWithAssignedJobTitle = jobTitleId => () => {
    const { DEACTIVATED } = USER_STATUSES;
    const params = {
      ...PEOPLE_DEFAULT_ORDERING,
      [EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
      [EXCLUDE_STATUS]: DEACTIVATED,
      [PARAMS.JOB_TITLE]: jobTitleId,
    };

    return http.get(API_USERS_BASIC, { params });
  };

  handleOpenJobTitleForm = (
    isEdit = false,
    initialData = INITIAL_JOB_TITLE_DATA
  ) =>
    this.setState({
      isOpened: true,
      isEdit,
      initialData: {
        ...INITIAL_JOB_TITLE_DATA,
        ...initialData,
      },
    });

  handleCloseJobTitleForm = () =>
    this.setState({
      isOpened: false,
      isEdit: false,
      initialData: INITIAL_JOB_TITLE_DATA,
      jobTitle: null,
      isDelete: false,
    });

  onSearch = value => {
    const text = trimString(value);

    this.setState(
      { [PARAMS.SEARCH]: text, isLoading: true },
      this.handleGetJobTitles
    );
  };

  handleApplyFilters = filters =>
    this.setState({ filters, isLoading: true }, this.handleGetJobTitles);

  handleAddJobTitle = values => {
    const { translations, accessControl } = this.props;
    const { isEdit } = this.state;
    const canManageJobTitle = hasAuthPermissionCheck(
      PERMISSION_CODES.JOB_TITLES_MANAGE,
      accessControl
    );
    const canManageSuccessionSettings = hasAuthPermissionCheck(
      PERMISSION_CODES.SUCCESSION_MANAGE,
      accessControl
    );

    const isKey = Boolean(values.is_key);

    const data = {
      ...(isEdit ? { id: values.id } : {}),
      ...(canManageJobTitle
        ? {
            name: trimString(values.name),
            description: values.description
              ? trimString(values.description)
              : null,
            tags: values.tags,
          }
        : {}),
      ...(canManageSuccessionSettings
        ? {
            job_family: values.job_family || null,
            job_type: values.job_type,
            is_key: isKey,
            criticality: isKey ? values.criticality : null,
          }
        : {}),
    };

    if (isEdit) {
      return http.patch(api_position(data.id), data).then(() => {
        this.handleGetJobTitles();
        showSuccessMessage(translations.successMessages.edit);
      });
    }
    const { AddJobTitle } = EVENT_ACTION_TYPES;
    return http.post(API_POSITIONS, data).then(() => {
      tagManagerDataLayer(AddJobTitle.action, AddJobTitle.name, values.name);
      this.handleGetJobTitles();
      showSuccessMessage(translations.successMessages.add);
    });
  };

  toggleAlertDialogVisibility = () => {
    this.setState(prevState => ({ isDelete: !prevState.isDelete }));
  };

  onDelete = jobTitle => {
    this.setState({ jobTitle, isDelete: true });
  };

  onDeleteJobTitle = () => {
    const { translations } = this.props;
    const { jobTitle } = this.state;

    return http.delete(api_position(jobTitle.id)).then(() => {
      this.handleGetJobTitles();
      this.handleCloseJobTitleForm();
      showSuccessMessage(translations.deleteJobTitleDialog.deleteSuccess);
    }, this.toggleAlertDialogVisibility);
  };

  onCreateTag = async tagName => {
    const { addMultipleTags } = this.props;
    const tags = await onSaveCreatableTag(tagName);
    const [tag] = tags.create;

    addMultipleTags({ categoryId: GENERAL_CATEGORY_ID, tags });

    return tag;
  };

  handlePreviewClick = async (e, jTitle) => {
    const { isDialogOpened } = this.state;

    if (!isDialogOpened) {
      e.stopPropagation();

      this.setState({ isDialogOpened: true, initialData: jTitle });
    } else {
      this.setState({
        isDialogOpened: false,
        initialData: INITIAL_JOB_TITLE_DATA,
      });
    }
  };

  importRecommendedPositions = () => {
    const { setJobTitles } = this.props;

    this.setState({ isLoadingImport: true });
    return http
      .get(API_IMPORT_POSITIONS, {
        params: JOB_TITLES_DEFAULT_PARAMS,
      })
      .then(({ data }) => {
        setJobTitles(data);
        this.setState({ isLoadingImport: false });
      });
  };

  handleCloseAssignJobTitleDialog = () => {
    this.setState({
      jobTitle: null,
      isAssignDialogOpened: false,
      jobTitleUsers: [],
    });
  };

  handleSave = async ({ assign_to }) => {
    const { jobTitle, jobTitleUsers, assignableUsers } = this.state;
    const { translations } = this.props;

    await http.patch(
      API_USERS_MULTIPLE,
      prepareUsersForUpdate(
        assignableUsers,
        assign_to,
        jobTitleUsers,
        jobTitle.id,
        USER_EMPLOYMENT_INFORMATION.JOB_TITLE
      )
    );
    this.handleGetJobTitles();
    showSuccessMessage(translations.jobTitleAssigning.successMessage);
  };

  handleAssignJobTitle = jobTitle => async () => {
    if (jobTitle.people_count !== 0) {
      const { data } = await this.getUsersWithAssignedJobTitle(jobTitle.id)();
      this.setState({
        jobTitleUsers: data || [],
      });
    }

    this.setState({
      jobTitle,
      isAssignDialogOpened: true,
    });
  };

  handleTranslations = () => {
    const { translations } = this.props;

    const { jobTitleAssigning } = translations;
    const formLabels = {
      assignTo: {
        ...jobTitleAssigning.formLabels.assignTo,
      },
    };
    return { ...jobTitleAssigning, formLabels };
  };

  renderJobTitles = (
    canManageJobTitle,
    canManageJobTitleUsers,
    canOpenJobTitleForm
  ) => {
    const {
      classes,
      accessControl,
      jobTitles,
      translations,
      goToProfilePage,
      successionSettings,
      organizationSettings,
    } = this.props;
    const { search, isLoading, isLoadingImport, filters } = this.state;
    const { users_see_succession } = organizationSettings;
    const canManageSuccessionSettings = hasAuthPermissionCheck(
      PERMISSION_CODES.SUCCESSION_MANAGE,
      accessControl
    );
    const noResultsMessage = canManageJobTitle
      ? translations.noResultsMessage
      : '';
    const filterIsActive = hasSelectedFiltersCheck(filters);

    return (
      <>
        {isArray(jobTitles) && !isArrayEmpty(jobTitles) && (
          <div className={classes.jobTitles}>
            {canManageJobTitle && (
              <PlaceholderButton
                label={translations.addJobTitleForm.add}
                onAdd={() => this.handleOpenJobTitleForm()}
              />
            )}
            {jobTitles.map(jTitle => (
              <JobTitleItem
                key={`job_title_${jTitle.id}`}
                data={jTitle}
                labels={translations.labels}
                familyLabel={jobFamilyLabel(
                  jTitle.job_family,
                  successionSettings?.job_families
                )}
                criticalityLabel={criticalityLabel(
                  jTitle.criticality,
                  successionSettings?.criticality_levels
                )}
                canSeeSuccession={
                  users_see_succession && canManageSuccessionSettings
                }
                isReadOnly={!canOpenJobTitleForm}
                handlePreviewClick={e => this.handlePreviewClick(e, jTitle)}
                canAccessUserProfileCheck={this.canAccessUserProfileCheck}
                goToUserProfilePage={goToProfilePage}
                getUsers={this.getUsersWithAssignedJobTitle(jTitle.id)}
                hasAddButton={canManageJobTitleUsers}
                onAddUser={this.handleAssignJobTitle(jTitle)}
                onJobTitleClick={() =>
                  this.handleOpenJobTitleForm(true, {
                    ...jTitle,
                    tags: getObjectToNumberArray(jTitle.tags),
                  })
                }
              />
            ))}
          </div>
        )}
        {!isLoading && isArray(jobTitles) && isArrayEmpty(jobTitles) && (
          <NotificationCard
            title={search || filterIsActive ? '' : translations.noResultsTitle}
            content={
              search || filterIsActive
                ? translations.noSearchResultsMessage
                : noResultsMessage
            }
            actionButtons={
              canManageJobTitle && !search && !filterIsActive
                ? [
                    {
                      label: translations.addRecommendedLabel,
                      type: 'rounded',
                      startIcon: <PlusIconWhite />,
                      onButtonClick: this.importRecommendedPositions,
                    },
                  ]
                : []
            }
            isLoading={isLoadingImport}
            loadingText={translations.loadingImportText}
          />
        )}
      </>
    );
  };

  render() {
    const {
      classes,
      accessControl,
      translations,
      categories,
      pageQuickFilters,
      successionSettings,
    } = this.props;
    const {
      isInitialLoad,
      isOpened,
      initialData,
      isEdit,
      isDelete,
      isDialogOpened,
      filters,
      search,
      isAssignDialogOpened,
      jobTitleUsers,
      assignableUsers,
    } = this.state;
    const canManageJobTitle = hasAuthPermissionCheck(
      PERMISSION_CODES.JOB_TITLES_MANAGE,
      accessControl
    );
    const canManageJobTitleUsers = hasAuthPermissionCheck(
      PERMISSION_CODES.JOB_TITLES_USERS_MANAGE,
      accessControl
    );
    const canManageSuccessionSettings = hasAuthPermissionCheck(
      PERMISSION_CODES.SUCCESSION_MANAGE,
      accessControl
    );
    const canOpenJobTitleForm =
      canManageJobTitle || canManageSuccessionSettings;

    return (
      !isInitialLoad && (
        <div>
          <div className={classes.stickySubHeader}>
            <Typography variant="body2" className={classes.description}>
              {canManageJobTitle
                ? translations.description
                : translations.descriptionReadOnly}
            </Typography>
            <div className={classes.subHeader}>
              <Filters
                translations={translations.filters}
                selectedFilters={filters}
                filters={preparePageFilters(
                  translations,
                  categories,
                  pageQuickFilters
                )}
                onApplyFilters={this.handleApplyFilters}
              />
              <div className={classes.actionsWrapper}>
                <Search
                  className={classes.search}
                  placeholder={translations.search}
                  value={search}
                  onChange={this.onSearch}
                />
                {canManageJobTitle && (
                  <CustomButton
                    className={classes.addButton}
                    type="addRoundedNew"
                    onClick={() => this.handleOpenJobTitleForm()}
                  >
                    {translations.addButtonLabel}
                  </CustomButton>
                )}
              </div>
            </div>
          </div>
          {this.renderJobTitles(
            canManageJobTitle,
            canManageJobTitleUsers,
            canOpenJobTitleForm
          )}
          <PreviewDescriptionDialog
            isOpened={isDialogOpened}
            title={initialData.name}
            description={initialData.description}
            onClose={() => {
              this.setState({
                isDialogOpened: false,
                initialData: INITIAL_JOB_TITLE_DATA,
              });
            }}
          />
          <CustomFormDrawer
            customFormClass={classNames(classes.form, {
              [classes.formWithSuccession]: canManageSuccessionSettings,
              [classes.formWithoutSuccession]: !canManageSuccessionSettings,
            })}
            initialData={initialData}
            isOpened={isOpened}
            isInitialValid={isEdit}
            translations={translations.addJobTitleForm}
            fields={prepareJobTitleFields(this.onCreateTag, classes, {
              canManageJobTitle,
              canManageSuccessionSettings,
            })}
            jobTitleTypes={prepareJobTitleTypeOptions(
              translations.addJobTitleForm.formLabels.job_type.options
            )}
            jobFamilies={prepareActiveOptions(successionSettings?.job_families)}
            criticalityLevels={prepareActiveOptions(
              successionSettings?.criticality_levels
            )}
            categories={categories}
            onClose={this.handleCloseJobTitleForm}
            onSave={this.handleAddJobTitle}
            onDelete={this.onDelete}
            hideDelete={!canManageJobTitle}
            isMiddleWidth
          />
          <CustomFormDrawer
            customContentClass={classes.drawerContent}
            translations={this.handleTranslations()}
            isOpened={isAssignDialogOpened}
            initialData={getInitialAssignedToUsersData(jobTitleUsers)}
            fields={ASSIGN_JOB_TITLE_TO_FORM_FIELDS}
            allUsers={assignableUsers}
            onClose={this.handleCloseAssignJobTitleDialog}
            onSave={this.handleSave}
            hasCancelButton
            hideDelete
            isInitialValid
          />
          <AlertDialog
            translations={translations.deleteJobTitleDialog}
            isOpened={isDelete}
            onClose={this.toggleAlertDialogVisibility}
            onConfirm={this.onDeleteJobTitle}
            isWarning
          />
        </div>
      )
    );
  }
}

JobTitlesPage.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  jobTitles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  pageQuickFilters: PropTypes.arrayOf(PropTypes.object).isRequired,
  successionSettings: PropTypes.shape({
    job_families: PropTypes.arrayOf(PropTypes.shape({})),
    criticality_levels: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  getJobTitles: PropTypes.func.isRequired,
  getTagsCategories: PropTypes.func.isRequired,
  getPageQuickFilters: PropTypes.func.isRequired,
  addMultipleTags: PropTypes.func.isRequired,
  clearJobTitles: PropTypes.func.isRequired,
  goToProfilePage: PropTypes.func.isRequired,
  clearTagsCategories: PropTypes.func.isRequired,
  clearPageQuickFilters: PropTypes.func.isRequired,
  getAllUsers: PropTypes.func.isRequired,
  getSuccessionJobFamilies: PropTypes.func.isRequired,
  getSuccessionCriticality: PropTypes.func.isRequired,
};

JobTitlesPage.defaultProps = {
  successionSettings: null,
};

export default withStyles(styles)(JobTitlesPage);
